import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import liff from "@line/liff";

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE_URL ||
  (process as any).env?.REACT_APP_API_BASE_URL ||
  "http://localhost:4000"; // 既定：開発API

const LIFF_ID =
  (import.meta as any).env?.VITE_LIFF_ID ||
  (process as any).env?.REACT_APP_LIFF_ID ||
  ""; // 必須：環境変数で設定

// クエリ取得ユーティリティ
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function AuthLine() {
  const nav = useNavigate();
  const query = useQuery();

  // 進捗とエラー表示（簡易）
  const [phase, setPhase] = useState<"init" | "login" | "posting" | "done" | "error">("init");
  const [message, setMessage] = useState<string>("");

  // 1) LIFF初期化 → 2) ログイン → 3) id_token取得 → 4) /api/auth/line へPOST → 5) リダイレクト
  useEffect(() => {
    (async () => {
      try {
        if (!LIFF_ID) {
          setPhase("error");
          setMessage("LIFF_ID が未設定です（VITE_LIFF_ID / REACT_APP_LIFF_ID）");
          return;
        }

        setPhase("init");
        setMessage("LIFF 初期化中…");
        await liff.init({ liffId: LIFF_ID });

        // 未ログインならLINEログインへ
        if (!liff.isLoggedIn()) {
          setPhase("login");
          setMessage("LINE ログインへリダイレクトします…");
          // state/redirectはURLに含めたまま戻ってくる
          liff.login();
          return; // 以降はログイン後のリロードで継続
        }

        // ログイン済 → id_token 取得
        const idToken = liff.getIDToken();
        if (!idToken) {
          setPhase("error");
          setMessage("id_token を取得できませんでした。");
          return;
        }

        // aff固定（クエリの aff があれば渡す。なければ空文字でOK）
        const aff = query.get("aff") || "";

        // optional: リダイレクト先（なければ / ）
        const redirect = query.get("redirect") || "/";

        setPhase("posting");
        setMessage("サーバーへ認証情報を送信しています…");

        const res = await fetch(`${API_BASE}/api/auth/line`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // ← Set-Cookie 受け取り
          body: JSON.stringify({ id_token: idToken, aff }),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          setPhase("error");
          setMessage(`認証APIエラー：${res.status} ${txt || res.statusText}`);
          return;
        }

        // ここでサーバーが Cookie を発行済み想定（セッション／JWT など）
        setPhase("done");
        setMessage("ログイン完了。遷移します…");

        // 少し待ってから遷移（Cookie反映の安全策）
        setTimeout(() => {
          nav(redirect, { replace: true });
        }, 200);
      } catch (e: any) {
        setPhase("error");
        setMessage(`想定外のエラー：${e?.message || String(e)}`);
      }
    })();
  }, [nav, query]);

  // シンプルな状態表示のみ（UIは最小）
  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <div style={{ fontWeight: 800, marginBottom: 8 }}>LINEログイン</div>
      <div style={{ fontSize: 14, opacity: 0.8 }}>
        {phase === "init" && "初期化中…"}
        {phase === "login" && "LINEへ遷移中…"}
        {phase === "posting" && "サーバー認証処理中…"}
        {phase === "done" && "完了しました。"}
        {phase === "error" && (
          <>
            <div style={{ color: "#c00", marginBottom: 8 }}>エラーが発生しました</div>
            <code style={{ fontSize: 12, wordBreak: "break-all" }}>{message}</code>
            <div style={{ marginTop: 12 }}>
              <button
                onClick={() => window.location.reload()}
                style={{ padding: "6px 12px", borderRadius: 6 }}
              >
                リロード
              </button>
            </div>
          </>
        )}
        {phase !== "error" && message && <div style={{ marginTop: 8 }}>{message}</div>}
      </div>
    </div>
  );
}
